import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';

const SEEN_KEY = 'adminNotificationsSeenAt';
const POLL_MS = 60000;

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 2rem;
  margin: -2rem -2rem 1.5rem;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TitleBlock = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const HospitalLine = styled.span`
  font-size: 0.75rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: ${(props) => (props.active ? '#eff6ff' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #dc2626;
  color: #ffffff;
  font-size: 0.6875rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.25rem 0.5rem 0.25rem 1rem;
  border: none;
  border-left: 1px solid #e2e8f0;
  background: transparent;
  cursor: pointer;
  text-align: left;
`;

const Avatar = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #0b63c5;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ProfileText = styled.span`
  display: flex;
  flex-direction: column;
  line-height: 1.25;

  @media (max-width: 640px) {
    display: none;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: ${(props) => props.width || '220px'};
  max-width: calc(100vw - 1.5rem);
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 31, 77, 0.16);
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9375rem;
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  color: #0b63c5;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
`;

const NoticeList = styled.div`
  max-height: 360px;
  overflow-y: auto;
`;

const NoticeItem = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f8fafc;
  background: ${(props) => (props.unread ? '#f8fbff' : '#ffffff')};
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: #ffffff;
  text-align: left;
  font-size: 0.9375rem;
  color: ${(props) => (props.danger ? '#dc2626' : '#334155')};
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
`;

function useOutsideClose(ref, open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onClose();
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [ref, open, onClose]);
}

const AdminTopBar = ({ title, user, onOpenProfile, onLogout }) => {
  const [notifications, setNotifications] = useState([]);
  const [seenAt, setSeenAt] = useState(() => Number(localStorage.getItem(SEEN_KEY)) || 0);
  const [bellOpen, setBellOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const bellRef = useRef(null);
  const menuRef = useRef(null);

  const closeBell = useCallback(() => setBellOpen(false), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useOutsideClose(bellRef, bellOpen, closeBell);
  useOutsideClose(menuRef, menuOpen, closeMenu);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await axios.get('/api/admin/notifications');
        if (!cancelled) setNotifications(response.data.notifications || []);
      } catch (error) {
        if (!cancelled) setNotifications([]);
      }
    };
    load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const isUnread = (notice) => new Date(notice.time).getTime() > seenAt;
  const unreadCount = notifications.filter(isUnread).length;

  const markAllRead = () => {
    const now = Date.now();
    localStorage.setItem(SEEN_KEY, String(now));
    setSeenAt(now);
  };

  const displayName = user?.fullName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Admin';
  const initial = displayName.trim().charAt(0).toUpperCase() || 'A';
  const hospitalIdLine = [
    user?.hospitalPublicId && `Hospital ID: ${user.hospitalPublicId}`,
    user?.hospitalCode && `Code: ${user.hospitalCode}`
  ].filter(Boolean).join(' · ');

  return (
    <Bar>
      <TitleBlock>
        <Title>{title}</Title>
        {(user?.hospitalName || hospitalIdLine) && (
          <HospitalLine title={user?.hospitalAddress || undefined}>
            {[user?.hospitalName, hospitalIdLine].filter(Boolean).join(' — ')}
          </HospitalLine>
        )}
      </TitleBlock>
      <Actions>
        <div ref={bellRef} style={{ position: 'relative' }}>
          <IconButton
            type="button"
            aria-label="Notifications"
            active={bellOpen}
            onClick={() => { setBellOpen((open) => !open); setMenuOpen(false); }}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#475569" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {unreadCount > 0 && <Badge>{unreadCount > 9 ? '9+' : unreadCount}</Badge>}
          </IconButton>
          {bellOpen && (
            <Dropdown width="360px">
              <DropdownHeader>
                <span>Notifications</span>
                {unreadCount > 0 && <LinkButton type="button" onClick={markAllRead}>Mark all read</LinkButton>}
              </DropdownHeader>
              <NoticeList>
                {notifications.length === 0 && (
                  <div style={{ padding: '1.5rem 1rem', color: '#94a3b8', fontSize: '0.875rem', textAlign: 'center' }}>
                    No activity in the last 7 days
                  </div>
                )}
                {notifications.map((notice) => (
                  <NoticeItem key={notice.id} unread={isUnread(notice)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: 600, color: notice.important ? '#b91c1c' : '#1e293b' }}>{notice.title}</span>
                      {isUnread(notice) && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#0b63c5', marginTop: 6, flexShrink: 0 }} />}
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: '#64748b', marginTop: 2 }}>{notice.description}</div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 4 }}>
                      {formatDistanceToNow(new Date(notice.time), { addSuffix: true })}
                    </div>
                  </NoticeItem>
                ))}
              </NoticeList>
            </Dropdown>
          )}
        </div>

        <div ref={menuRef} style={{ position: 'relative' }}>
          <ProfileButton
            type="button"
            aria-label="Account menu"
            onClick={() => { setMenuOpen((open) => !open); setBellOpen(false); }}
          >
            <Avatar>{initial}</Avatar>
            <ProfileText>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>{displayName}</span>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{user?.adminId || 'Hospital admin'}</span>
            </ProfileText>
            <svg viewBox="0 0 12 12" width="12" height="12" fill="none"><path d="M2 4l4 4 4-4" stroke="#94A3B8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </ProfileButton>
          {menuOpen && (
            <Dropdown>
              <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>{displayName}</div>
                {user?.email && <div style={{ color: '#64748b', fontSize: '0.75rem', wordBreak: 'break-all' }}>{user.email}</div>}
                {user?.hospitalName && <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{user.hospitalName}</div>}
                {user?.hospitalPublicId && <div style={{ color: '#94a3b8', fontSize: '0.75rem', fontFamily: 'monospace' }}>Hospital ID: {user.hospitalPublicId}</div>}
                {user?.hospitalCode && <div style={{ color: '#94a3b8', fontSize: '0.75rem', fontFamily: 'monospace' }}>Code: {user.hospitalCode}</div>}
              </div>
              <MenuItem type="button" onClick={() => { setMenuOpen(false); onOpenProfile(); }}>Hospital profile</MenuItem>
              <MenuItem type="button" danger onClick={() => { setMenuOpen(false); onLogout(); }}>Logout</MenuItem>
            </Dropdown>
          )}
        </div>
      </Actions>
    </Bar>
  );
};

export default AdminTopBar;
